'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import {
    SiteSpecialExhibition,
    SiteSpecialExhibitionInput,
} from "@/app/lib/generated/graphql-types";
import { getSiteSpecialExhibitions } from "@/app/features/sites/special-exhibitions/data-access/queries";
import { updateSiteSpecialExhibitions } from "@/app/features/sites/special-exhibitions/data-access/mutations";
import { SiteSpecialExhibitionsSchema } from "@/app/features/sites/special-exhibitions/types/schema";
import type { SiteSpecialExhibitionsEditable } from "@/app/features/sites/special-exhibitions/types/types";

const SiteSpecialExhibitionsEditorContext = createContext<
    UseSingleTypeEditorType<SiteSpecialExhibitionsEditable> | undefined
>(undefined);

export function useSiteSpecialExhibitionsEditor() {
    const context = useContext(SiteSpecialExhibitionsEditorContext);
    if (!context) {
        throw new Error(
            "useSiteSpecialExhibitionsEditor must be used within SiteSpecialExhibitionsEditorProvider",
        );
    }
    return context;
}

export default function SiteSpecialExhibitionsEditorProvider({
    children,
    isDraftModeEnabled,
}: {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}) {
    const editor = useSingleTypeEditor<
        SiteSpecialExhibition,
        SiteSpecialExhibitionsEditable,
        SiteSpecialExhibitionInput
    >({
        schema: SiteSpecialExhibitionsSchema,
        loadEntity: getSiteSpecialExhibitions,
        update: updateSiteSpecialExhibitions,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteSpecialExhibitionsEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => void editor.discardChanges()}
            />
        </SiteSpecialExhibitionsEditorContext.Provider>
    );
}
